import { useState } from 'react';

import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight, IconCalculator } from '@iwoca/icons';
import { Modal } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { addMonths, format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';
import { OverpayLoanSVG } from '../../../../../../svgComponents/overpayLoanSVG/OverpayLoanSVG';
import { useOffersPageVersion } from '../../../offersPage.helpers';
import { RepaymentCalculator } from '../../FlexiApprovedView/ComparisonAndRepaymentModal/RepaymentCalculator/RepaymentCalculator';

export const RepaymentCalculatorButton = ({
    selectedOffer,
    drawdownAmount,
    variant
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
    variant: 'desktop' | 'mobile';
}) => {
    const offersPageVersion = useOffersPageVersion();
    const EARLY_REPAYMENT_MONTHS = 9;
    const [isRepaymentCalculatorOpen, setIsRepaymentCalculatorOpen] =
        useState(false);

    const { data } = useDrawdownRepaymentSchedule({
        offerId: selectedOffer?.offer_id ?? '',
        drawdownAmount: drawdownAmount ?? 0,
        earlyFinalRepaymentDate: format(
            addMonths(new Date(), EARLY_REPAYMENT_MONTHS),
            'yyyy-MM-dd'
        ),
        enabled: Boolean(
            offersPageVersion === '2.1' && selectedOffer && drawdownAmount
        )
    });

    const totalPayable = data?.summary.total_payable ?? null;

    const openCalculator = () => {
        tracking.ampli.earlyRepaymentCalculatorOpened({
            calculator_button: 'loan summary',
            offers_page_version: offersPageVersion
        });
        setIsRepaymentCalculatorOpen(true);
    };

    if (offersPageVersion === '2.0' && variant === 'mobile') {
        return null;
    }

    return (
        <>
            {offersPageVersion === '2.1' && selectedOffer && drawdownAmount ? (
                <div
                    className="my-s rounded-s bg-core-95 p-l min-sm:my-xl"
                    data-testid="repayment-calculator-widget"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-s">
                            <OverpayLoanSVG className="h-[20px] w-[20px] shrink-0" />
                            <Text className={`text-xs font-bold min-sm:text-s`}>
                                <FormattedMessage
                                    id="_.loan_summary.total_if_repaid_9_months"
                                    defaultMessage="Total if repaid at 9 months:"
                                />
                            </Text>
                        </div>
                        <Text className={`text-xs font-bold min-sm:text-s`}>
                            {totalPayable !== null ? (
                                <FormattedCurrency value={totalPayable} />
                            ) : (
                                '£--.--'
                            )}
                        </Text>
                    </div>

                    <button
                        className="mt-m flex w-full items-center justify-between rounded-s border-2 border-primary-10 bg-structure-100 px-m py-s hover:bg-secondary-95"
                        onClick={openCalculator}
                    >
                        <div className="flex items-center gap-s">
                            <IconCalculator
                                className="shrink-0 text-primary-10"
                                fontSize="20px"
                            />
                            <Text className={`text-xs font-med min-sm:text-s`}>
                                <FormattedMessage
                                    id="_.loan_summary.explore_savings"
                                    defaultMessage="Explore your potential savings"
                                />
                            </Text>
                        </div>
                        <IconAngleRight
                            className="shrink-0 text-primary-10"
                            fontSize="20px"
                        />
                    </button>
                </div>
            ) : offersPageVersion === '2.0' ? (
                <div className="my-xl flex items-start gap-s rounded-s bg-primary-20 p-m">
                    <IconCalculator
                        className="shrink-0 text-secondary-80"
                        fontSize="16px"
                    />
                    <div>
                        <Text className="text-s text-structure-100">
                            <FormattedMessage
                                id="_.loan_summary.no_early_repayment_fees"
                                defaultMessage="Remember, no early repayment fees."
                            />
                        </Text>
                        {selectedOffer && drawdownAmount && (
                            <button
                                className="mt-xs text-s font-bold text-structure-100"
                                onClick={openCalculator}
                            >
                                <FormattedMessage
                                    id="_.loan_summary.calculate_potential_savings"
                                    defaultMessage="Calculate your potential savings"
                                />
                            </button>
                        )}
                    </div>
                </div>
            ) : null}

            {selectedOffer && drawdownAmount && (
                <Modal
                    headerBar
                    onClose={() => {
                        tracking.ampli.earlyRepaymentCalculatorClosed();
                        setIsRepaymentCalculatorOpen(false);
                    }}
                    isOpen={isRepaymentCalculatorOpen}
                    size="large"
                    closeIcon
                >
                    <RepaymentCalculator
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                        defaultPreset="9-months"
                    />
                </Modal>
            )}
        </>
    );
};
